import React, { forwardRef, useContext, useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { MotionProps, PanInfo, motion, useAnimation } from 'motion/react';
import { useMediaMatches } from '@frontend/responsiveness';
import type { CommonHTMLAttributes } from '@frontend/types';
import { theme } from '@frontend/theme';
import { FontColorType } from '../../helpers';
import { useThemeValues } from '../../hooks';
import { ConfirmBadgeIcon, ErrorBadgeIcon, InfoBadgeIcon, WarningBadgeIcon, XIconSmall } from '../../icon';
import { Text } from '../text';
import { TextButton } from '../text-button';
import {
  alertActionContainerStyle,
  closeButtonStyle,
  containerStyle,
  textStyle as alertTextStyle,
  getBorderLeftColor,
  actionButtonStyles,
} from './alert.styles';
import { AlertsVariantContext } from './alerts.provider';
import { actionButtonVariants, alertTypeIconVariants, buttonAnimation, newButtonAnimation } from './constants';
import { AlertType, AlertAction } from './types';

type AlertProps = Omit<CommonHTMLAttributes, 'onClick'> & {
  children: React.ReactNode;
  id?: string;
  onClick?: (id: string) => void;
  type: AlertType;
  action?: AlertAction;
  index?: number;
} & Omit<MotionProps, 'onClick'>;

const iconMap = {
  error: ErrorBadgeIcon,
  info: InfoBadgeIcon,
  success: ConfirmBadgeIcon,
  warning: WarningBadgeIcon,
};

const iconColorMap: { [key: string]: FontColorType } = {
  error: 'error',
  info: 'primary',
  success: 'success',
  warning: 'warn',
};

const toastVariants = {
  hidden: { opacity: 0, y: 50, scale: 0.9 },
  show: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.4, ease: 'easeOut', staggerChildren: 0.4 } },
  exit: { opacity: 0, y: -50, scale: 0.9, zIndex: -1, transition: { duration: 0.3, ease: 'easeIn' } },
  slideOut: { x: 400, opacity: 0, transition: { duration: 0.1 } },
};

export const Alert = forwardRef<HTMLParagraphElement, AlertProps>(
  ({ children, id, onClick, type, action, index, ...rest }: AlertProps, ref) => {
    const controls = useAnimation();
    const { matches } = useMediaMatches();
    const isMobileScreen = matches.useXSmallMax();
    const { spacing, borderRadius } = useThemeValues();
    const [slideOut, setSlideOut] = useState(false);
    const newAlert = useContext(AlertsVariantContext) === 'experimental';
    const [hovered, setHovered] = useState(false);
    const Icon = iconMap[type];

    let handleClick;
    if (typeof onClick === 'function') {
      handleClick = () => onClick(id ?? '');
    }

    useEffect(() => {
      if (handleClick) {
        controls.start('show');
      }
    }, []);

    const handleDragEnd = (_, { offset }: PanInfo) => {
      if (offset.x > 200) {
        setSlideOut(true);
        controls.start('slideOut').then(handleClick);
      } else {
        controls.start({ x: 0, transition: { duration: 0.2 } }); // Snap back if not far enough
      }
    };

    const handleDrag = (_, { offset }: PanInfo) => {
      if (offset.x < 0) {
        controls.start({ x: 0 });
      }
    };

    const buttonProps = newAlert
      ? {
          initial: 'hidden',
          animate: hovered ? 'hovering' : 'hidden',
          variants: newButtonAnimation,
        }
      : {
          initial: 'hidden',
          animate: 'show',
          variants: buttonAnimation,
        };

    const textStyle = alertTextStyle(type, newAlert);
    return (
      <motion.div
        css={[
          css`
            position: relative;
            margin-bottom: ${spacing(1)};
            display: flex;
            gap: ${spacing(0.5)};
            width: 100%;
            border-radius: ${borderRadius.medium};
          `,
          index &&
            css`
              z-index: ${index};
            `,
        ]}
        {...rest}
        drag={isMobileScreen && !!handleClick ? 'x' : undefined}
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={0.5}
        onDrag={handleDrag}
        onDragEnd={handleDragEnd}
        animate={!!handleClick ? controls : undefined}
        initial={!!handleClick ? 'hidden' : undefined}
        onHoverStart={() => setHovered(true)}
        onHoverEnd={() => setHovered(false)}
        exit={!!handleClick ? (slideOut ? {} : 'exit') : undefined}
        layout={!!handleClick ? 'position' : false}
        variants={!!handleClick ? toastVariants : undefined}
        ref={ref}
      >
        {!!handleClick && <XButton onClose={handleClick} {...buttonProps} />}
        {newAlert && (
          <motion.div
            css={closeButtonStyle}
            variants={alertTypeIconVariants}
            initial='initial'
            animate={hovered ? 'hovering' : 'initial'}
            exit={'exit'}
          >
            <Icon color={iconColorMap[type]} />
          </motion.div>
        )}
        <motion.div css={containerStyle} aria-live={type === 'error' || type === 'warning' ? 'assertive' : 'polite'}>
          <motion.div
            css={[
              css`
                padding: ${spacing(2, 2, 2, 2.5)};
                display: flex;
                width: 100%;
                align-items: center;
                gap: ${spacing(2)};
              `,
              newAlert &&
                css`
                  border: 2px solid ${getBorderLeftColor(type)};
                  border-radius: ${borderRadius.medium};
                  padding: ${spacing(1.5, 1.5, 1.5, 2.5)};
                  position: relative;
                `,
            ]}
          >
            {!newAlert && (
              <Icon
                css={
                  action &&
                  css`
                    margin: ${spacing(1, 0)};
                  `
                }
                color={iconColorMap[type]}
              />
            )}
            <Text size='medium' css={textStyle}>
              {children}
            </Text>
          </motion.div>
          {action && !newAlert && (
            <motion.div css={alertActionContainerStyle}>
              <TextButton
                onClick={(e) => {
                  action.onClick(e);
                  handleClick();
                }}
                css={css`
                  padding: ${spacing(1)};
                `}
                trackingId={action.trackingId}
              >
                {action.label}
              </TextButton>
            </motion.div>
          )}
        </motion.div>
        {action && newAlert && (
          <motion.div
            css={actionButtonStyles}
            initial='initial'
            exit='exit'
            animate={hovered ? 'hovering' : 'initial'}
            variants={actionButtonVariants}
          >
            <TextButton
              css={css`
                font-size: ${spacing(1.75)};
                cursor: pointer;
                color: ${theme.colors.neutral90};
                font-weight: ${theme.font.weight.regular};
                :hover {
                  background: ${theme.colors.white};
                }
              `}
              trackingId={action.trackingId}
              onClick={(e) => {
                action.onClick(e);
                handleClick();
              }}
            >
              {action.label}
            </TextButton>
          </motion.div>
        )}
      </motion.div>
    );
  }
);
Alert.displayName = 'Alert';

type XButtonProps = {
  onClose: () => void;
} & MotionProps;

export const XButton = ({ onClose, ...rest }: XButtonProps) => {
  return (
    <motion.button
      {...rest}
      exit='exit'
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
      css={closeButtonStyle}
    >
      <XIconSmall />
    </motion.button>
  );
};
